import { NOME_TIPO_SOCIETARIO, TIPOS_SOCIETARIOS } from '@social/shared';
import { classeSeloTipo, moeda, type SociedadeResumo } from './sociedade-comum';

/**
 * A aba Resumo de `Sociedades`: a MESMA leva filtrada, agrupada por tipo
 * societário.
 *
 * Só uma tabela — o pedido tem quatro recortes (situação, cliente,
 * representante) e por isso ganha um componente de agrupamento próprio no
 * Adonai; sociedade tem um recorte natural (tipo), que não justifica mais que
 * isto.
 *
 * Em arquivo à parte por causa do teto de linhas de `sociedades.tsx` — não
 * por peso próprio.
 */
export function ResumoPorTipo({ sociedades }: { sociedades: SociedadeResumo[] }) {
  const total = sociedades.length;
  const linhas = TIPOS_SOCIETARIOS.map((t) => {
    const doTipo = sociedades.filter((s) => s.tipo === t);
    return {
      tipo: t,
      contagem: doTipo.length,
      capital: doTipo.reduce((soma, s) => soma + s.capitalCentavos, 0),
    };
  }).filter((l) => l.contagem > 0);

  if (linhas.length === 0) {
    return (
      <div className="rounded-xl border border-tinta-100 bg-superficie p-6 text-center">
        <p className="font-medium text-tinta-900">Nada para resumir</p>
        <p className="text-tinta-500 mx-auto mt-1 max-w-sm text-sm">
          Mude o período ou os filtros para ver o resumo por tipo.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-tinta-100 bg-superficie">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-tinta-500">
            <tr className="border-tinta-100 border-b">
              <th
                scope="col"
                className="px-3 py-2.5 text-left font-bold whitespace-nowrap"
              >
                Tipo
              </th>
              <th
                scope="col"
                className="px-3 py-2.5 text-right font-bold whitespace-nowrap"
              >
                Sociedades
              </th>
              <th
                scope="col"
                className="px-3 py-2.5 text-right font-bold whitespace-nowrap"
              >
                Capital social
              </th>
              <th
                scope="col"
                className="px-3 py-2.5 text-right font-bold whitespace-nowrap"
              >
                % do total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-tinta-100/60">
            {linhas.map((l) => (
              <tr key={l.tipo}>
                <td className="px-3 py-[var(--pad-y)]">
                  <span
                    className={classeSeloTipo(l.tipo)}
                    title={NOME_TIPO_SOCIETARIO[l.tipo]}
                  >
                    {l.tipo}
                  </span>
                </td>
                <td className="px-3 py-[var(--pad-y)] text-right tabular-nums">
                  {l.contagem}
                </td>
                <td className="px-3 py-[var(--pad-y)] text-right tabular-nums">
                  {moeda(l.capital)}
                </td>
                <td className="px-3 py-[var(--pad-y)] text-right tabular-nums">
                  {total > 0 ? `${Math.round((l.contagem / total) * 100)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
